import type { EntityData } from '@adornis/baseql/entities/types.js';
import { EntityPickerInput } from '@adornis/entity-picker/client/entity-picker-input.js';
import { AdornisFilter } from '@adornis/filter/AdornisFilter.js';
import { DropdownSelectionInput } from '@adornis/formfield/fields/dropdown-selection-input.js';
import { TextInput } from '@adornis/formfield/fields/text-input.js';
import type { FunctionalForm } from '@adornis/forms/use-form.js';
import { stateful } from '@adornis/functional-lit/stateful.js';
import { and } from '@adornis/validation/functions/and.js';
import { FormGridEntry } from '@martinus/styles/client/FormGridEntry.js';
import { validateRVNR } from '@martinus/validation/rentenVersicherungsNummer.js';
import { sgNonOptional } from '@martinus/validation/sgNonOptional.js';
import { html, nothing } from 'lit';
import { GermanInsuranceTypes } from '../../../../db/enums/misc-enums.js';
import { HealthInsurance } from '../../../../healthinsurance/entities/HealthInsurance.js';
import type { Person } from '../../../entities/person.js';

export const EmployeeSectionSozialVersicherungForm = stateful((props: { form: FunctionalForm<EntityData<Person>> }) => {
  if (!props.form.document) return nothing;

  return html`
    <div class="form-grid">
      ${FormGridEntry({
        label: 'Krankenversicherungsstatus',
        render: DropdownSelectionInput({
          ...props.form.field('mitarbeiterDaten', 'anSozialVersicherung', 'versicherungsStatus'),
          selectables: Object.values(GermanInsuranceTypes),
          placeholder: 'Kein Krankenversicherungsstatus hinterlegt',
          placeholderMode: 'prefix',
          clearable: true,
        }),
      })}
      ${FormGridEntry({
        label: 'Rentenversicherungsnummer',
        render: TextInput({
          ...props.form.field('mitarbeiterDaten', 'anSozialVersicherung', 'rentenVersicherungsNr'),
          placeholder: 'Rentenversicherungsnummer',
          placeholderMode: 'hide',
          clearable: true,
        }),
      })}
      ${
        props.form.document.mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus !== GermanInsuranceTypes.PRIVAT
          ? FormGridEntry({
              label: 'Krankenkasse',
              render: EntityPickerInput({
                ...props.form.field('mitarbeiterDaten', 'anSozialVersicherung', 'krankenkassenID'),
                class: HealthInsurance,
                filter: new AdornisFilter({
                  searchFields: ['bezeichnung'],
                  sortingFields: ['bezeichnung'],
                  sortingDirections: ['desc'],
                }),
                selectionSet: { bezeichnung: 1, ort: 1 },
                renderString: (item: HealthInsurance) => `${item.bezeichnung} (${item.ort})`,
                placeholder: 'Krankenkasse nicht hinterlegt',
                placeholderMode: 'hide',
                clearable: true,
              }),
            })
          : nothing
      }
    </div>
  `;
});

export const EmployeeSectionSozialVersicherungFormValidator = {
  versicherungsStatus: sgNonOptional(),
  rentenVersicherungsNr: and(sgNonOptional(), validateRVNR),
  krankenkassenID: options => {
    if (options.source.mitarbeiterDaten?.anSozialVersicherung?.versicherungsStatus !== GermanInsuranceTypes.PRIVAT) {
      return sgNonOptional()(options);
    }
    return true;
  },
};
